import { CountUp } from '@/components/ui/count-up';
import type { ModuleKey } from '@ruby/shared';
import { Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { useState, type CSSProperties, type HTMLAttributes, type ReactNode } from 'react';
import { Card } from '@/components/ui/misc';
import { usePermissions } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { MODULE_UI } from '@/app/modules';
import { useSession } from '@/lib/session';

/**
 * Dashboard building blocks shared by module dashboards: a "needs a decision" strip, figure cards,
 * titled panels, horizontal bar lists and a monthly bar chart. Links respect screen permissions.
 */
export type DashLink = { screen: string; search?: Record<string, string> };

const fmt = (n: number, decimals = 2) => n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
export const aed = (n: number) => (n < 0 ? `(AED ${fmt(-n)})` : `AED ${fmt(n)}`);
const compact = (n: number) => (Math.abs(n) >= 1e6 ? `${(n / 1e6).toFixed(2)}M` : Math.abs(n) >= 1e3 ? `${(n / 1e3).toFixed(0)}K` : n.toFixed(0));
const monthLabel = (m: string) => new Date(`${m}-01T00:00:00Z`).toLocaleDateString('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' });

/** Extra props (onPointerMove for fx-spot, style, data-tip …) pass through to the link or the plain span. */
export function ScreenLink({ module, to, className, children, ...rest }: { module: ModuleKey; to: DashLink; className?: string; children: ReactNode } & Omit<HTMLAttributes<HTMLElement>, 'className' | 'children'>) {
  const { can } = usePermissions();
  if (!can(module, to.screen, 'view')) return <span className={className} {...rest}>{children}</span>;
  return <Link to="/m/$module/$screen" params={{ module, screen: to.screen }} search={to.search} className={className} {...rest}>{children}</Link>;
}

export interface DecisionItem { icon: ReactNode; tone: string; title: string; detail?: ReactNode; to?: DashLink; hide?: boolean }

/** Strip of things waiting on someone, each opening its list. */
export function DecisionStrip({ module, items, footer }: { module: ModuleKey; items: DecisionItem[]; footer?: ReactNode }) {
  const shown = items.filter((i) => !i.hide);
  return (
    <Card className="overflow-hidden">
      <p className="border-b border-border bg-surface-2 px-4 py-2 text-[11px] font-semibold tracking-wider text-fg-muted uppercase">Needs attention</p>
      <div className="grid divide-border sm:grid-cols-2 sm:divide-x xl:grid-cols-4 [&>*]:border-b [&>*]:border-border">
        {shown.map((i) => {
          const body = (
            <div className="flex h-full items-start gap-2.5 px-4 py-3">
              <span className={cn('mt-0.5 transition-transform duration-300 ease-(--ease-spring) group-hover/ds:scale-125 group-hover/ds:-rotate-6 [&_svg]:size-4', i.tone)}>{i.icon}</span>
              <div className="min-w-0">
                <p className="text-[13px] font-semibold">{i.title}</p>
                {i.detail && <p className="text-xs text-fg-muted">{i.detail}</p>}
                {i.to && <p className="mt-1 inline-flex items-center gap-0.5 text-xs font-medium text-primary">Open <ArrowRight className="size-3 transition-transform duration-200 group-hover/ds:translate-x-1" /></p>}
              </div>
            </div>
          );
          return i.to ? <ScreenLink key={i.title} module={module} to={i.to} className="group/ds block transition-colors duration-200 hover:bg-primary-soft/40">{body}</ScreenLink> : <div key={i.title}>{body}</div>;
        })}
      </div>
      {footer}
    </Card>
  );
}

const TONE_BAR = { primary: 'before:bg-primary', success: 'before:bg-success', warning: 'before:bg-warning', danger: 'before:bg-danger', neutral: 'before:bg-[#4c6fd1]' } as const;
const TONE_GLOW = { primary: 'after:bg-primary/12', success: 'after:bg-success/12', warning: 'after:bg-warning/14', danger: 'after:bg-danger/12', neutral: 'after:bg-[#4c6fd1]/12' } as const;
const TONE_DOT = { primary: 'bg-primary', success: 'bg-success', warning: 'bg-warning', danger: 'bg-danger', neutral: 'bg-[#4c6fd1]' } as const;

export type KpiTone = keyof typeof TONE_BAR;

/** A headline figure's content: coloured dot and label, the figure counting up, the hint; an arrow when it opens a screen. */
export function KpiBody({ label, value, hint, tone, negative, linked }: { label: string; value: string; hint?: ReactNode; tone: KpiTone; negative?: boolean; linked?: boolean }) {
  return (
    <>
      <p className="relative flex items-center gap-1.5 text-[12px] font-semibold tracking-wide text-fg-muted uppercase"><span className={cn('size-2 rounded-full', TONE_DOT[tone])} />{label}</p>
      <p className={cn('relative mt-2 text-[27px] leading-tight font-extrabold tracking-tight tabular-nums', negative && 'text-danger')}><CountUp value={value} /></p>
      {hint && <p className="relative mt-1.5 text-xs text-fg-subtle">{hint}</p>}
      {linked && <ArrowRight className="absolute top-4 right-4 size-4 -translate-x-1 text-fg-subtle opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />}
    </>
  );
}

/** A headline figure card: coloured strip on top and a soft glow of the same colour; lifts when it opens a screen. */
export const kpiClass = (tone: KpiTone, linked?: boolean) =>
  cn(
    'group relative block overflow-hidden rounded-2xl border border-border bg-surface p-5 shadow-(--shadow-card)',
    'before:absolute before:inset-x-0 before:top-0 before:h-[3px] before:origin-left before:transition-transform before:duration-500 after:pointer-events-none after:absolute after:-top-10 after:-right-10 after:size-32 after:rounded-full after:blur-2xl after:transition-transform after:duration-700 after:ease-(--ease-out) hover:after:scale-150',
    TONE_BAR[tone],
    TONE_GLOW[tone],
    linked && 'fx-lift hover:border-border-strong',
  );

export function Kpi({ module, label, value, hint, tone, to, negative }: { module: ModuleKey; label: string; value: string; hint?: ReactNode; tone: KpiTone; to?: DashLink; negative?: boolean }) {
  const body = <KpiBody label={label} value={value} hint={hint} tone={tone} negative={negative} linked={!!to} />;
  return to ? <ScreenLink module={module} to={to} className={kpiClass(tone, true)}>{body}</ScreenLink> : <div className={kpiClass(tone)}>{body}</div>;
}

/** A dashboard panel's heading row: title, note, and a "Details" link (the link element is given by the caller). */
export function PanelHead({ title, sub, link }: { title: string; sub?: string; link?: ReactNode }) {
  return (
    <div className="mb-4 flex items-start justify-between gap-3">
      <div>
        <p className="text-[15px] font-bold tracking-tight">{title}</p>
        {sub && <p className="mt-0.5 text-xs text-fg-subtle">{sub}</p>}
      </div>
      {link}
    </div>
  );
}

export const detailsLinkClass = 'group/d inline-flex shrink-0 items-center gap-1 rounded-full bg-primary-soft px-2.5 py-1 text-xs font-semibold text-primary transition-colors hover:bg-primary hover:text-primary-fg';

export function Panel({ module, title, sub, link, children }: { module: ModuleKey; title: string; sub?: string; link?: DashLink; children: ReactNode }) {
  return (
    <Card className="rounded-2xl p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <p className="text-[15px] font-bold tracking-tight">{title}</p>
          {sub && <p className="mt-0.5 text-xs text-fg-subtle">{sub}</p>}
        </div>
        {link && <ScreenLink module={module} to={link} className="group/d inline-flex shrink-0 items-center gap-1 rounded-full bg-primary-soft px-2.5 py-1 text-xs font-semibold text-primary transition-colors hover:bg-primary hover:text-primary-fg">Details <ArrowRight className="size-3 transition-transform group-hover/d:translate-x-0.5" /></ScreenLink>}
      </div>
      {children}
    </Card>
  );
}

/** Horizontal bars: label above, value at the right. */
export function HBars({ module, rows, color = 'bg-primary/70', empty = 'Nothing yet.', decimals = 2 }: { module: ModuleKey; rows: { key: string; label: string; value: number; sub?: string; to?: DashLink }[]; color?: string; empty?: string; decimals?: number }) {
  const max = Math.max(...rows.map((r) => r.value), 1);
  if (!rows.length) return <p className="text-[13px] text-fg-subtle">{empty}</p>;
  return (
    <ul className="flex flex-col gap-3">
      {rows.map((r, idx) => (
        <li key={r.key} title={`${r.label}: ${fmt(r.value, decimals)}`}>
          <div className="mb-1 flex items-baseline justify-between gap-3 text-[13px]">
            {r.to ? <ScreenLink module={module} to={r.to} className="break-words hover:text-primary hover:underline">{r.label}</ScreenLink> : <span className="break-words">{r.label}</span>}
            <span className="shrink-0 font-medium tabular-nums">{fmt(r.value, decimals)}{r.sub && <span className="ml-1.5 text-xs font-normal text-fg-subtle">{r.sub}</span>}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-surface-2"><div className={cn('fx-grow-x h-2 rounded-full', color)} style={{ width: `${Math.max((Math.max(r.value, 0) / max) * 100, r.value ? 1.5 : 0)}%`, '--i': idx } as CSSProperties} /></div>
        </li>
      ))}
    </ul>
  );
}

/** One series per month, shared axis, value on hover. */
export function MonthBars({ rows, label, color = '#1f8a5b' }: { rows: { month: string; value: number }[]; label: string; color?: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(...rows.map((r) => r.value), 1);
  const ticks = [1, 0.5, 0].map((f) => max * f);
  const h = 200;
  return (
    <div className="flex gap-2">
      <div className="flex flex-col justify-between text-right text-[10px] text-fg-subtle tabular-nums" style={{ height: h }}>{ticks.map((t) => <span key={t}>{compact(t)}</span>)}</div>
      <div className="relative min-w-0 flex-1">
        <div className="pointer-events-none absolute inset-x-0 top-0 flex flex-col justify-between" style={{ height: h }}>{ticks.map((t) => <span key={t} className="border-t border-dashed border-border" />)}</div>
        <div className="relative flex items-end gap-[6px]" style={{ height: h }} onMouseLeave={() => setHover(null)} role="img" aria-label={rows.map((r) => `${monthLabel(r.month)}: ${fmt(r.value)}`).join('; ')}>
          {rows.map((r, i) => (
            <div key={r.month} className={cn('relative flex h-full min-w-0 flex-1 items-end justify-center rounded-sm', hover === i && 'bg-surface-2')} onMouseEnter={() => setHover(i)}>
              <div className="fx-grow-y w-[60%] max-w-7 rounded-t-[4px] transition-opacity duration-200" style={{ background: color, height: `${Math.max((Math.max(r.value, 0) / max) * 100, r.value ? 1 : 0)}%`, opacity: hover === null || hover === i ? 1 : 0.55, '--i': i } as CSSProperties} />
              {hover === i && (
                <div className={cn('fx-pop pointer-events-none absolute bottom-full z-10 mb-1 w-max rounded-lg border border-border bg-surface/95 px-2.5 py-1.5 text-[11px] shadow-(--shadow-pop) backdrop-blur', i > rows.length - 4 ? 'right-0' : 'left-0')}>
                  <p className="font-semibold">{monthLabel(r.month)}</p>
                  <p>{label} <b className="tabular-nums">{fmt(r.value)}</b></p>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="mt-1.5 flex gap-[6px]">{rows.map((r) => <span key={r.month} className="min-w-0 flex-1 truncate text-center text-[10px] text-fg-subtle">{monthLabel(r.month)}</span>)}</div>
      </div>
    </div>
  );
}

/**
 * The banner at the top of every module dashboard: the module's icon, its title, a greeting with the date, and room
 * on the right for the main actions or a live fact. Brand gradient with soft shapes; text stays readable in both themes.
 */
export function ModuleHero({ module, title, subtitle, children }: { module: ModuleKey; title: string; subtitle?: ReactNode; children?: ReactNode }) {
  const Icon = MODULE_UI[module].icon;
  const name = useSession((s) => s.boot?.user.fullName.split(' ')[0]) ?? '';
  const h = new Date().getHours();
  const hello = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Dubai' });
  return (
    <section className="fx-rise relative mb-5 overflow-hidden rounded-[22px] bg-[linear-gradient(120deg,#0b2a1d_0%,#0e6b43_45%,#10a36a_100%)] px-6 py-6 text-white shadow-[0_22px_44px_-22px_rgb(14_107_67/0.75)] sm:px-7 dark:bg-[linear-gradient(120deg,#071a2b_0%,#0b3b45_50%,#0f766e_100%)]">
      <div aria-hidden className="pointer-events-none absolute -top-20 -right-10 size-72 rounded-full bg-emerald-300/20 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -bottom-24 left-1/3 size-64 rounded-full bg-cyan-300/15 blur-3xl" />
      <svg aria-hidden viewBox="0 0 200 200" className="pointer-events-none absolute -right-6 -bottom-10 size-56 text-white/[.07]"><circle cx="100" cy="100" r="90" fill="none" stroke="currentColor" strokeWidth="14" /><circle cx="100" cy="100" r="50" fill="none" stroke="currentColor" strokeWidth="10" /></svg>
      <div className="relative flex flex-wrap items-center gap-5">
        <span className="flex size-14 shrink-0 items-center justify-center rounded-2xl bg-white/15 shadow-[inset_0_1px_0_rgb(255_255_255/0.25)] ring-1 ring-white/20 backdrop-blur [&_svg]:size-7"><Icon strokeWidth={1.8} /></span>
        <div className="min-w-0 flex-1">
          <p className="text-[12.5px] font-semibold text-emerald-100/90">{hello}{name ? `, ${name}` : ''} · {today}</p>
          <h1 className="mt-0.5 text-[26px] leading-tight font-extrabold tracking-tight">{title}</h1>
          {subtitle && <p className="mt-1 text-[13px] text-emerald-50/85">{subtitle}</p>}
        </div>
        {children && <div className="relative flex flex-wrap items-center gap-2">{children}</div>}
      </div>
    </section>
  );
}
